<template>
  <div class="w-full text-slate-700" v-if="ipInfo && ipInfo.success">
    <div
      class="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-4 flex flex-col gap-5"
    >
      <!-- Section Header -->
      <div
        class="flex items-center justify-between pb-3 border-b border-slate-100"
      >
        <div class="flex items-center gap-2.5">
          <div
            class="w-8 h-8 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center text-sm font-bold shadow-xs"
          >
            <i class="fa-solid fa-earth-americas"></i>
          </div>
          <div>
            <h2 class="text-base font-bold text-slate-800 m-0">
              Geolocation Information
            </h2>
            <p class="text-xs text-slate-400 m-0">
              Physical location and network details inferred from IP
            </p>
          </div>
        </div>

        <span
          class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-mono font-bold bg-slate-100 text-slate-700 border border-slate-200/60"
        >
          IP: {{ ipInfo.ip }}
        </span>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
        <!-- 1. Map Section -->
        <div
          class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col gap-3 min-h-[340px]"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <i class="fa-solid fa-map-location-dot text-blue-600 text-xs"></i>
              <h3
                class="text-xs font-bold uppercase tracking-wider text-slate-700 m-0"
              >
                Location Map
              </h3>
            </div>
            <button
              @click="resetCoordinates"
              class="px-2.5 py-1 bg-white hover:bg-slate-100 text-slate-600 border border-slate-200/60 rounded-lg text-[11px] font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
              title="Reset map view"
            >
              <i class="fa-solid fa-crosshairs text-[10px]"></i>
              <span>Reposition</span>
            </button>
          </div>

          <div
            id="map"
            class="w-full flex-1 min-h-[280px] rounded-xl overflow-hidden border border-slate-200 z-10 shadow-xs"
          ></div>
        </div>

        <!-- 2. Info Cards (Location & Network) -->
        <div class="flex flex-col gap-4">
          <!-- Location Details -->
          <div
            class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col gap-3"
          >
            <div
              class="flex items-center gap-2 pb-2 border-b border-slate-200/60"
            >
              <i class="fa-solid fa-location-dot text-rose-500 text-xs"></i>
              <h3
                class="text-xs font-bold uppercase tracking-wider text-slate-700 m-0"
              >
                Location Details
              </h3>
            </div>
            <div class="grid grid-cols-2 gap-2 text-xs">
              <div
                class="flex flex-col bg-white border border-slate-200/60 rounded-xl p-2"
              >
                <span class="text-[10px] text-slate-400 font-semibold uppercase"
                  >Country</span
                >
                <span
                  class="font-bold text-slate-800 flex items-center gap-1.5 mt-0.5"
                >
                  <img
                    :src="ipInfo.country_flag"
                    class="w-4 h-3 rounded-xs object-cover"
                    :alt="ipInfo.country"
                  />
                  {{ ipInfo.country }} ({{ ipInfo.country_code }})
                </span>
              </div>
              <div
                class="flex flex-col bg-white border border-slate-200/60 rounded-xl p-2"
              >
                <span class="text-[10px] text-slate-400 font-semibold uppercase"
                  >City</span
                >
                <span class="font-bold text-slate-800 mt-0.5">{{
                  ipInfo.city || "-"
                }}</span>
              </div>
              <div
                class="flex flex-col bg-white border border-slate-200/60 rounded-xl p-2"
              >
                <span class="text-[10px] text-slate-400 font-semibold uppercase"
                  >Region</span
                >
                <span class="font-bold text-slate-800 mt-0.5">{{
                  ipInfo.region || "-"
                }}</span>
              </div>
              <div
                class="flex flex-col bg-white border border-slate-200/60 rounded-xl p-2"
              >
                <span class="text-[10px] text-slate-400 font-semibold uppercase"
                  >Continent</span
                >
                <span class="font-bold text-slate-800 mt-0.5">{{
                  ipInfo.continent || "-"
                }}</span>
              </div>
            </div>
          </div>

          <!-- Network Details -->
          <div
            class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col gap-3"
          >
            <div
              class="flex items-center gap-2 pb-2 border-b border-slate-200/60"
            >
              <i class="fa-solid fa-network-wired text-blue-600 text-xs"></i>
              <h3
                class="text-xs font-bold uppercase tracking-wider text-slate-700 m-0"
              >
                Network Details
              </h3>
            </div>
            <div class="grid grid-cols-2 gap-2 text-xs">
              <div
                class="flex flex-col bg-white border border-slate-200/60 rounded-xl p-2"
              >
                <span class="text-[10px] text-slate-400 font-semibold uppercase"
                  >ISP</span
                >
                <span
                  class="font-bold text-slate-800 truncate mt-0.5"
                  :title="ipInfo.isp"
                  >{{ ipInfo.isp || "-" }}</span
                >
              </div>
              <div
                class="flex flex-col bg-white border border-slate-200/60 rounded-xl p-2"
              >
                <span class="text-[10px] text-slate-400 font-semibold uppercase"
                  >Organization</span
                >
                <span
                  class="font-bold text-slate-800 truncate mt-0.5"
                  :title="ipInfo.org"
                  >{{ ipInfo.org || "-" }}</span
                >
              </div>
              <div
                class="flex flex-col bg-white border border-slate-200/60 rounded-xl p-2"
              >
                <span class="text-[10px] text-slate-400 font-semibold uppercase"
                  >ASN</span
                >
                <span class="font-bold text-slate-800 mt-0.5">{{
                  ipInfo.asn || "-"
                }}</span>
              </div>
              <div
                class="flex flex-col bg-white border border-slate-200/60 rounded-xl p-2"
              >
                <span class="text-[10px] text-slate-400 font-semibold uppercase"
                  >IP Type</span
                >
                <span class="font-bold text-slate-800 mt-0.5">{{
                  ipInfo.type || "-"
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: new URL(
    "leaflet/dist/images/marker-icon-2x.png",
    import.meta.url,
  ).href,
  iconUrl: new URL("leaflet/dist/images/marker-icon.png", import.meta.url).href,
  shadowUrl: new URL("leaflet/dist/images/marker-shadow.png", import.meta.url)
    .href,
});

export default {
  name: "IpConnectionInfo",
  props: {
    ipInfo: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      map: null,
    };
  },
  computed: {
    ...mapGetters(["isMenuOpen"]),
  },
  mounted() {
    this.initMap();
  },
  methods: {
    resetCoordinates() {
      if (this.map && this.ipInfo) {
        const lat = this.ipInfo.latitude;
        const lon = this.ipInfo.longitude;
        this.map.setView([lat, lon], 10);
      }
    },
    initMap() {
      const lat = this.ipInfo?.latitude;
      const lon = this.ipInfo?.longitude;
      if (!lat || !lon) return;

      this.$nextTick(() => {
        if (this.map) this.map.remove();
        this.map = L.map("map", {
          zoomControl: true,
          scrollWheelZoom: true,
          doubleClickZoom: true,
          boxZoom: true,
          keyboard: true,
          dragging: true,
          touchZoom: true,
        }).setView([lat, lon], 10);

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 18,
        }).addTo(this.map);

        const customIcon = L.divIcon({
          html: '<div style="background-color: #3b82f6; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);"><i class="fas fa-map-marker-alt text-xs"></i></div>',
          className: "custom-marker-container",
          iconSize: [24, 24],
          iconAnchor: [12, 24],
          popupAnchor: [0, -24],
        });

        L.marker([lat, lon], { icon: customIcon })
          .addTo(this.map)
          .bindPopup(
            `
            <div style="font-family: inherit; font-size: 12px; color: #1e293b;">
              <h5 style="margin: 0 0 4px 0; font-size: 13px; font-weight: bold;"><i class="fas fa-map-marker-alt text-blue-500"></i> ${
                this.ipInfo.city
              }, ${this.ipInfo.country}</h5>
              <p style="margin: 2px 0;"><strong>IP:</strong> ${
                this.ipInfo.ip
              }</p>
              <p style="margin: 2px 0;"><strong>ISP:</strong> ${
                this.ipInfo.isp
              }</p>
              <p style="margin: 2px 0;"><strong>Coords:</strong> ${lat.toFixed(
                4,
              )}, ${lon.toFixed(4)}</p>
            </div>
          `,
          )
          .openPopup();

        L.circle([lat, lon], {
          radius: 25000,
          color: "#3b82f6",
          fillColor: "#3b82f6",
          fillOpacity: 0.1,
          weight: 2,
          opacity: 0.8,
        }).addTo(this.map);
      });
    },
  },
};
</script>
